import { IMealPeriod } from "@dining/api-client";

/**
 * @param id - The meal period ID to validate
 * @returns true if the ID appears to be a valid database ID
 */
export const isValidDatabaseId = (id: string | undefined): boolean => {
  if (!id) return false;

  return (
    // Check if it's a valid GUID format (common for database IDs)
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
  );
};

/**
 * Processes and cleans meal periods for hours of operation updates
 * Handles smart detection of new vs existing meal periods
 * @param mealPeriods - Array of meal periods to process
 * @returns Array of cleaned meal periods ready for API submission
 * @throws Error if no meal periods are provided
 */
interface CleanMealPeriod {
  name: string | undefined;
  startsOn: string | undefined;
  endsOn: string | undefined;
  customPeriod: string | undefined;
  hoursOfOperation: string | undefined;
  timeStamp: string;
  id?: string;
}

export const processMealPeriodsForUpdate = (mealPeriods: IMealPeriod[]): CleanMealPeriod[] => {
  // Process meal periods - smart detection of new vs existing
  const cleanMealPeriods = mealPeriods.map((period) => {
    const hasValidId = isValidDatabaseId(period.id);

    const cleanPeriod: CleanMealPeriod = {
      name: period.name,
      startsOn: period.startsOn,
      endsOn: period.endsOn,
      customPeriod: period.customPeriod,
      hoursOfOperation: period.hoursOfOperation,
      // Fix timeStamp issue: ensure it's never null for new periods
      timeStamp: hasValidId ? period.timeStamp : new Date().toISOString(),
    };

    if (hasValidId) {
      // If the meal period has a valid database id, preserve it in the clean object.
      // This means the meal period already exists in the database and will be updated.
      cleanPeriod.id = period.id;
    } else {
      // If the meal period does not have a valid id, do not assign an id property.
      // This means the meal period is new and will be created by the API (which will assign an id).
    }

    return cleanPeriod;
  });

  // If there are no meal periods, return an empty array.
  // Previously this threw an error which prevented clearing all meal periods from the UI.
  // Returning an empty list lets the caller send an empty payload to the API to clear hours.
  return cleanMealPeriods;
};
